import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';

import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { formatShortDate } from 'util/formatting';

import { LL } from 'i18n/i18n.service';
// The four week-count periods belong to the Body Composition overview (#5456). This type has two
// more copies that must change with it: `pages/progress/progress-interface.ts` and the inline
// union on `timePeriod` in `components/cs-progress-chart/cs-progress-chart.ts`.
export type TimePeriod = 'week' | 'month' | 'quarter' | 'year' | 'all' | '2weeks' | '4weeks' | '8weeks' | '12weeks';

/** which group of periods the toolbar offers */
export enum TimePeriodSet {
  /** Week, Month, Quarter, Year, All: what the toolbar always offered */
  Classic = 'Classic',
  /** 2w, 4w, 8w, 12w, Year, All: the Body Composition overview's periods (#5456) */
  WeekCounts = 'WeekCounts',
}

export interface DateRangeChangeEvent {
  timePeriod: TimePeriod;
  startDate: DateTime;
  endDate: DateTime;
  /** true while the range still ends at the present moment, false once the user paged back (#5456) */
  endsToday: boolean;
}

/** How far one press of the previous or next arrow moves the range, and how long the range itself is. */
export function timePeriodDuration(timePeriod: TimePeriod): { weeks?: number; months?: number; years?: number } {
  switch (timePeriod) {
    case 'week':
      return { weeks: 1 };
    case '2weeks':
      return { weeks: 2 };
    case '4weeks':
      return { weeks: 4 };
    case '8weeks':
      return { weeks: 8 };
    case '12weeks':
      return { weeks: 12 };
    case 'month':
      return { months: 1 };
    case 'quarter':
      return { months: 3 };
    case 'year':
      return { years: 1 };
    case 'all':
      return { years: 1 }; // "all" has no length of its own; the arrows move it a year
  }
}

@Component({
  selector: 'cs-date-range-toolbar',
  templateUrl: 'cs-date-range-toolbar.html',
  host: { 'class': 'date-range-toolbar' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsDateRangeToolbar implements OnInit, OnChanges {
  protected LL = LL;
  protected TimePeriodSet = TimePeriodSet;

  @Input() timePeriod: TimePeriod = 'week';
  /** which group of periods to offer; without it, the classic five */
  @Input() periodSet: TimePeriodSet = TimePeriodSet.Classic;
  @Input() earliestDataDate?: DateTime;
  @Output() dateRangeChange = new EventEmitter<DateRangeChangeEvent>();

  protected startDate: DateTime;
  protected endDate: DateTime;
  protected dateRangeText = '';
  /** the period the current range was worked out for; tells a tap on our own tabs from a change made by the host */
  private rangePeriod?: TimePeriod;

  constructor(
    private debug: DebugService,
    private settings: SettingsService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.calculateDateRange();
    this.updateDateRangeText();
    this.emitDateRangeChange();
  }

  ngOnChanges(changes: SimpleChanges) {
    // if the host changed the period or the group of periods after the first render, start over
    // from "now" and emit (#5456: a config switch flipping while the page is open; the host
    // reloads on that emit, also when the period itself stays Year or All). the first change is
    // ngOnInit's job, which runs right after it. a host that only copies back the period this
    // toolbar just emitted changes nothing: the range is already worked out for it, and
    // starting over would emit, and make the host load, a second time.
    const periodChanged = !!changes['timePeriod'] && !changes['timePeriod'].firstChange
      && this.timePeriod !== this.rangePeriod;
    const periodSetChanged = !!changes['periodSet'] && !changes['periodSet'].firstChange;
    if (periodChanged || periodSetChanged) {
      this.calculateDateRange();
      this.updateDateRangeText();
      this.emitDateRangeChange();
      this.cd.detectChanges();
      return;
    }
    // if earliestDataDate changed and we're in "all" mode, recalculate
    if (changes['earliestDataDate'] && this.timePeriod === 'all') {
      this.calculateDateRange();
      this.updateDateRangeText();
      this.emitDateRangeChange();
      this.cd.detectChanges();
    }
  }

  // calculate start and end dates based on time period
  private calculateDateRange() {
    const now = this.debug.getNowLocal();
    this.endDate = now;
    this.rangePeriod = this.timePeriod;

    // "all" starts at the earliest data when the host knows it; every other period, and "all"
    // without that date, reaches one period length back from now
    if (this.timePeriod === 'all' && this.earliestDataDate) {
      this.startDate = this.earliestDataDate.startOf('day');
    } else {
      this.startDate = now.minus(timePeriodDuration(this.timePeriod));
    }
  }

  /** the text on one period's tab */
  protected periodLabel(period: TimePeriod) {
    switch (period) {
      case 'week': return LL.dateRange.week();
      case 'month': return LL.dateRange.month();
      case 'quarter': return LL.dateRange.quarter();
      case 'year': return LL.dateRange.year();
      case 'all': return LL.dateRange.all();
      case '2weeks': return LL.dateRange.twoWeeks();
      case '4weeks': return LL.dateRange.fourWeeks();
      case '8weeks': return LL.dateRange.eightWeeks();
      case '12weeks': return LL.dateRange.twelveWeeks();
    }
  }

  // update date range display text
  private updateDateRangeText() {
    const startFormatted = formatShortDate(this.startDate, this.settings);
    const endFormatted = formatShortDate(this.endDate, this.settings);
    this.dateRangeText = `${startFormatted} - ${endFormatted}`;
  }

  // emit date range change event
  private emitDateRangeChange() {
    this.dateRangeChange.emit({
      timePeriod: this.timePeriod,
      startDate: this.startDate,
      endDate: this.endDate,
      // the arrows move both ends by whole periods, so the range ends today exactly when the
      // next arrow would step into the future
      endsToday: this.isNextDisabled(),
    });
  }

  // handle time period tab change
  protected handleTimePeriodChange(period: TimePeriod | Event) {
    // cs-segment emits the value directly, not an event
    if (typeof period === 'string') {
      this.timePeriod = period as TimePeriod;
      this.calculateDateRange();
      this.updateDateRangeText();
      this.emitDateRangeChange();
      this.cd.detectChanges();
    }
  }

  // handle date navigation (previous period)
  protected handleDateNavigationPrev() {
    const duration = this.getTimePeriodDuration();
    this.startDate = this.startDate.minus(duration);
    this.endDate = this.endDate.minus(duration);
    this.updateDateRangeText();
    this.emitDateRangeChange();
    this.cd.detectChanges();
  }

  // handle date navigation (next period)
  protected handleDateNavigationNext() {
    // if already disabled, do nothing
    if (this.isNextDisabled()) {
      return;
    }

    const duration = this.getTimePeriodDuration();
    this.startDate = this.startDate.plus(duration);
    this.endDate = this.endDate.plus(duration);
    this.updateDateRangeText();
    this.emitDateRangeChange();
    this.cd.detectChanges();
  }

  // get duration object for current time period
  private getTimePeriodDuration() {
    return timePeriodDuration(this.timePeriod);
  }

  // check if forward navigation is disabled (would go into the future)
  protected isNextDisabled(): boolean {
    const duration = this.getTimePeriodDuration();
    const now = this.debug.getNowLocal();
    const newEndDate = this.endDate.plus(duration);
    return newEndDate > now;
  }

}
